import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import CircularProgress from "@mui/material/CircularProgress";
import { useNavigate, useParams } from "react-router-dom";
import MDBox from "shared/ui/Box/MDBox";
import MDTypography from "shared/ui/Typography/MDTypography";
import logger from "utils/logger";
import { createEmptyProgramState } from "features/planner/model/programState";
import {
  clearDay as clearProgramDay,
  clearAllExerciseFields,
  clearExerciseFields,
  clearProgramContent,
  getOccupiedLocations,
  moveExercise as moveProgramExercise,
  placeExercise as placeExerciseInState,
  removeExercise as removeExerciseFromState,
  setDayLabel as setProgramDayLabel,
  setProgramNotes,
  swapExercises as swapProgramExercises,
  updateExercise as updateExerciseInState,
  updateExercises,
  updateProgramMetadata,
} from "features/planner/model/programStateOperations";
import {
  areProgramStatesEqual,
  isProgramStateValid,
} from "features/planner/model/programStateValidation";
import {
  createProgram,
  generateProgramPdf as requestProgramPdf,
  getProgram,
  updateProgram,
} from "features/planner/api/programApi";
import {
  programDtoToState,
  programStateToPdfDto,
  programStateToSaveDto,
} from "features/planner/api/programStateMapper";
import {
  getExistingProgramDraftIdentity,
  getNewProgramDraftIdentity,
  readProgramDraft,
  removeProgramDraft,
} from "features/planner/persistence/programDraftStorage";
import { UNSAVED_CHANGES_BYPASS_STATE } from "features/planner/state/navigationGuard";
import { isDemoMode } from "shared/config/appConfig";
import PlannerContext from "./PlannerContext";
import useAutosave from "./useAutosave";

const parseProgramId = (routeProgramId) => {
  if (!/^[1-9]\d*$/.test(routeProgramId || "")) return null;

  const programId = Number(routeProgramId);
  return Number.isSafeInteger(programId) ? programId : null;
};

export default function PlannerProvider({ children }) {
  const { programId: routeProgramId } = useParams();
  const navigate = useNavigate();
  const isNewProgramRoute = routeProgramId === "new";
  const parsedProgramId = isNewProgramRoute ? null : parseProgramId(routeProgramId);
  const draftIdentity = useMemo(() => {
    if (isNewProgramRoute) return getNewProgramDraftIdentity();
    if (parsedProgramId !== null) return getExistingProgramDraftIdentity(parsedProgramId);

    return null;
  }, [isNewProgramRoute, parsedProgramId]);
  const pendingCanonicalRouteRef = useRef(null);

  const [programState, setProgramState] = useState(createEmptyProgramState);
  const [savedProgramState, setSavedProgramState] = useState(createEmptyProgramState);

  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [hydratedProgramIdentity, setHydratedProgramIdentity] = useState(null);
  const [isProgramLoading, setIsProgramLoading] = useState(true);
  const [programLoadError, setProgramLoadError] = useState(null);

  const applySavedProgramState = useCallback((canonicalState) => {
    setProgramState(canonicalState);
    setSavedProgramState(canonicalState);
  }, []);

  useEffect(() => {
    const pendingCanonicalRoute = pendingCanonicalRouteRef.current;

    if (pendingCanonicalRoute?.programId === routeProgramId) {
      pendingCanonicalRouteRef.current = null;
      applySavedProgramState(pendingCanonicalRoute.programState);
      setHydratedProgramIdentity(routeProgramId);
      setProgramLoadError(null);
      setIsProgramLoading(false);
      return undefined;
    }

    const abortController = new AbortController();

    setIsProgramLoading(true);
    setProgramLoadError(null);
    setIsEditMode(false);
    setSelectedSlots([]);

    const hydrateProgram = async () => {
      if (isNewProgramRoute) {
        const emptyProgramState = createEmptyProgramState();
        const autosavedProgramState = readProgramDraft(draftIdentity);

        setSavedProgramState(emptyProgramState);
        setProgramState(autosavedProgramState || emptyProgramState);
        setHydratedProgramIdentity(routeProgramId);
        setIsProgramLoading(false);
        return;
      }

      if (parsedProgramId === null) {
        const emptyProgramState = createEmptyProgramState();

        setProgramState(emptyProgramState);
        setSavedProgramState(emptyProgramState);
        setHydratedProgramIdentity(routeProgramId);
        setProgramLoadError("El identificador de la rutina no es válido.");
        setIsProgramLoading(false);
        return;
      }

      try {
        logger.info("Loading program", parsedProgramId);

        const data = await getProgram(parsedProgramId, abortController.signal);
        const canonicalState = programDtoToState(data);

        if (abortController.signal.aborted) return;
        if (String(canonicalState.id) !== String(parsedProgramId)) {
          throw new Error("Loaded program id does not match the requested route");
        }

        const autosavedProgramState = readProgramDraft(draftIdentity);

        setSavedProgramState(canonicalState);
        setProgramState(autosavedProgramState || canonicalState);
        setHydratedProgramIdentity(routeProgramId);
        setIsProgramLoading(false);
      } catch (err) {
        if (err.name === "AbortError") return;

        logger.error("Failed to load program", err);

        const emptyProgramState = createEmptyProgramState();
        setProgramState(emptyProgramState);
        setSavedProgramState(emptyProgramState);
        setHydratedProgramIdentity(routeProgramId);
        setProgramLoadError("No se pudo cargar la rutina solicitada.");
        setIsProgramLoading(false);
      }
    };

    hydrateProgram();

    return () => abortController.abort();
  }, [applySavedProgramState, draftIdentity, isNewProgramRoute, parsedProgramId, routeProgramId]);

  const placeExercise = useCallback((day, slotIndex, exercise) => {
    setProgramState((currentState) =>
      placeExerciseInState(currentState, { dayKey: day, slotIndex }, exercise)
    );
  }, []);

  const removeExercise = useCallback((day, slotIndex) => {
    setProgramState((currentState) =>
      removeExerciseFromState(currentState, { dayKey: day, slotIndex })
    );
  }, []);

  const moveExercise = useCallback((fromDay, fromIndex, toDay, toIndex) => {
    setProgramState((currentState) =>
      moveProgramExercise(
        currentState,
        { dayKey: fromDay, slotIndex: fromIndex },
        { dayKey: toDay, slotIndex: toIndex }
      )
    );
  }, []);

  const swapExercises = useCallback((firstDay, firstIndex, secondDay, secondIndex) => {
    setProgramState((currentState) =>
      swapProgramExercises(
        currentState,
        { dayKey: firstDay, slotIndex: firstIndex },
        { dayKey: secondDay, slotIndex: secondIndex }
      )
    );
  }, []);

  const updateExercise = useCallback((day, slotIndex, fields) => {
    setProgramState((currentState) =>
      updateExerciseInState(currentState, { dayKey: day, slotIndex }, fields)
    );
  }, []);

  const clearDay = useCallback((dayKey) => {
    setProgramState((currentState) => clearProgramDay(currentState, dayKey));
  }, []);

  const clearAll = useCallback(() => {
    setProgramState((currentState) => clearProgramContent(currentState));
  }, []);

  const updateProgramField = useCallback((field, value) => {
    setProgramState((currentState) => updateProgramMetadata(currentState, { [field]: value }));
  }, []);

  const updateProgramNotes = useCallback((text) => {
    setProgramState((currentState) => setProgramNotes(currentState, text));
  }, []);

  const setDayLabel = useCallback((dayKey, label) => {
    setProgramState((currentState) => setProgramDayLabel(currentState, dayKey, label));
  }, []);

  const generateProgramPdf = useCallback(async () => {
    return requestProgramPdf(programStateToPdfDto(programState));
  }, [programState]);

  const saveProgram = useCallback(
    async ({ navigateAfterCreate = true } = {}) => {
      if (isDemoMode) {
        throw new Error("Program saving is disabled in demo mode.");
      }

      logger.info("Saving program");

      const isUpdate = programState.id !== null;
      const saveDto = programStateToSaveDto(programState);
      const savedProgram = isUpdate
        ? await updateProgram(programState.id, saveDto)
        : await createProgram(saveDto);
      const canonicalState = programDtoToState(savedProgram);

      if (isNewProgramRoute && canonicalState.id === null) {
        throw new Error("Saved program response is missing its id");
      }

      if (draftIdentity) {
        removeProgramDraft(draftIdentity);
      }

      applySavedProgramState(canonicalState);

      if (isNewProgramRoute && navigateAfterCreate) {
        const canonicalProgramId = String(canonicalState.id);
        pendingCanonicalRouteRef.current = {
          programId: canonicalProgramId,
          programState: canonicalState,
        };
        navigate(`/planner/${canonicalProgramId}`, {
          replace: true,
          state: UNSAVED_CHANGES_BYPASS_STATE,
        });
      }

      logger.info("Program saved successfully");
      return canonicalState;
    },
    [applySavedProgramState, draftIdentity, isNewProgramRoute, navigate, programState]
  );

  const discardCurrentAutosave = useCallback(() => {
    if (draftIdentity) {
      removeProgramDraft(draftIdentity);
    }
  }, [draftIdentity]);

  const enterEditMode = useCallback(() => {
    setIsEditMode(true);
    setSelectedSlots([]);
  }, []);

  const exitEditMode = useCallback(() => {
    setIsEditMode(false);
    setSelectedSlots([]);
  }, []);

  const toggleSlotSelection = useCallback((dayKey, slotIndex) => {
    setSelectedSlots((previousSelection) => {
      const exists = previousSelection.some(
        (selection) => selection.dayKey === dayKey && selection.slotIndex === slotIndex
      );

      if (exists) {
        return previousSelection.filter(
          (selection) => !(selection.dayKey === dayKey && selection.slotIndex === slotIndex)
        );
      }

      return [...previousSelection, { dayKey, slotIndex }];
    });
  }, []);

  const clearSelectedSlots = useCallback(() => {
    setSelectedSlots([]);
  }, []);

  const selectWholeDay = useCallback(
    (dayKey) => {
      setSelectedSlots((previousSelection) => {
        const daySlots = programState.days[dayKey]?.slots || [];
        const filledSlotIndexes = daySlots
          .map((slot, slotIndex) => (slot ? slotIndex : null))
          .filter((slotIndex) => slotIndex !== null);
        const selectedSlotIndexes = previousSelection
          .filter((selection) => selection.dayKey === dayKey)
          .map((selection) => selection.slotIndex);
        const allSelected =
          filledSlotIndexes.length > 0 &&
          filledSlotIndexes.every((slotIndex) => selectedSlotIndexes.includes(slotIndex));

        if (allSelected) {
          return previousSelection.filter((selection) => selection.dayKey !== dayKey);
        }

        const toAdd = filledSlotIndexes
          .filter((slotIndex) => !selectedSlotIndexes.includes(slotIndex))
          .map((slotIndex) => ({ dayKey, slotIndex }));

        return [...previousSelection, ...toAdd];
      });
    },
    [programState]
  );

  const applyBatchEdit = useCallback(
    (fields) => {
      setProgramState((currentState) => updateExercises(currentState, selectedSlots, fields));
      setSelectedSlots([]);
    },
    [selectedSlots]
  );

  const selectAllSlots = useCallback(() => {
    setSelectedSlots(getOccupiedLocations(programState));
  }, [programState]);

  const clearBatchField = useCallback(
    (field) => {
      setProgramState((currentState) => clearExerciseFields(currentState, selectedSlots, field));
    },
    [selectedSlots]
  );

  const clearAllBatchFields = useCallback(() => {
    setProgramState((currentState) => clearAllExerciseFields(currentState, selectedSlots));
  }, [selectedSlots]);

  const hasUnsavedChanges = useMemo(
    () => !areProgramStatesEqual(programState, savedProgramState),
    [programState, savedProgramState]
  );

  const isProgramValid = useMemo(() => isProgramStateValid(programState), [programState]);

  const hasPendingCanonicalRoute = pendingCanonicalRouteRef.current?.programId === routeProgramId;
  const hasCurrentProgramIdentity =
    hydratedProgramIdentity === routeProgramId || hasPendingCanonicalRoute;
  const isHydrated = !isProgramLoading && !programLoadError && hasCurrentProgramIdentity;

  useAutosave(draftIdentity, programState, 500, isHydrated && hasUnsavedChanges);

  useEffect(() => {
    if (isHydrated && !hasUnsavedChanges && draftIdentity) {
      removeProgramDraft(draftIdentity);
    }
  }, [draftIdentity, hasUnsavedChanges, isHydrated]);

  const value = useMemo(
    () => ({
      programState,

      updateProgramField,
      updateProgramNotes,
      setDayLabel,
      isProgramValid,

      placeExercise,
      removeExercise,
      moveExercise,
      swapExercises,
      updateExercise,
      clearDay,
      clearAll,
      generateProgramPdf,
      hasUnsavedChanges,
      saveProgram,
      discardCurrentAutosave,

      isEditMode,
      enterEditMode,
      exitEditMode,
      selectedSlots,
      toggleSlotSelection,
      clearSelectedSlots,
      selectWholeDay,
      applyBatchEdit,
      selectAllSlots,
      clearBatchField,
      clearAllBatchFields,
    }),
    [
      applyBatchEdit,
      clearAll,
      clearAllBatchFields,
      clearBatchField,
      clearDay,
      clearSelectedSlots,
      discardCurrentAutosave,
      enterEditMode,
      exitEditMode,
      generateProgramPdf,
      hasUnsavedChanges,
      isEditMode,
      isProgramValid,
      moveExercise,
      placeExercise,
      programState,
      removeExercise,
      saveProgram,
      selectAllSlots,
      selectWholeDay,
      selectedSlots,
      setDayLabel,
      swapExercises,
      toggleSlotSelection,
      updateExercise,
      updateProgramField,
      updateProgramNotes,
    ]
  );

  if (isProgramLoading || !hasCurrentProgramIdentity) {
    return (
      <MDBox
        sx={{
          height: "100%",
          minHeight: 160,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
      >
        <CircularProgress size={22} />
        <MDTypography variant="body2" color="text">
          Cargando programa...
        </MDTypography>
      </MDBox>
    );
  }

  if (programLoadError) {
    return <div role="alert">{programLoadError}</div>;
  }

  return <PlannerContext.Provider value={value}>{children}</PlannerContext.Provider>;
}

PlannerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
